// src/components/shop/WebsiteScraper.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { Globe, AlertCircle, CheckCircle, X, Loader, Image as ImageIcon } from 'lucide-react';

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.82);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.25s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const Modal = styled.div`
  background: ${p => p.theme?.colors?.surface || '#1a1a1a'};
  border: 2px solid ${p => p.theme?.colors?.accent || '#800000'};
  border-radius: 16px;
  padding: 1.75rem;
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  animation: slideUp 0.25s ease;
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    padding: 1.25rem;
    max-height: 88vh;
    border-radius: 12px;
  }
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme?.colors?.accent || '#800000'}60; border-radius: 2px; }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}30`};
  h2 {
    margin: 0;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme?.colors?.text || '#fff'};
  cursor: pointer;
  padding: 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const UrlRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  input {
    flex: 1;
    background: ${p => p.theme?.colors?.surface || '#1a1a1a'};
    border: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}50`};
    border-radius: 8px;
    color: ${p => p.theme?.colors?.text || '#fff'};
    padding: 0.65rem 0.9rem;
    font-size: 0.9rem;
    outline: none;
    &:focus { border-color: ${p => p.theme?.colors?.accent || '#800000'}; }
    &::placeholder { opacity: 0.4; }
  }
`;

const ScanBtn = styled.button`
  background: ${p => p.disabled ? 'rgba(255,255,255,0.08)' : (p.theme?.colors?.accentGradient || p.theme?.colors?.accent || '#800000')};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.4rem;
  opacity: ${p => p.disabled ? 0.45 : 1};
  white-space: nowrap;
  transition: opacity 0.15s;
`;

const StatusMsg = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 0.9rem;
  background: ${p =>
    p.type === 'error'   ? 'rgba(244,67,54,0.12)'  :
    p.type === 'success' ? 'rgba(76,175,80,0.12)'  :
    'rgba(33,150,243,0.10)'};
  border: 1px solid ${p =>
    p.type === 'error'   ? 'rgba(244,67,54,0.3)'  :
    p.type === 'success' ? 'rgba(76,175,80,0.3)'  :
    'rgba(33,150,243,0.25)'};
  color: ${p =>
    p.type === 'error'   ? '#ef5350' :
    p.type === 'success' ? '#66bb6a' :
    '#64b5f6'};
  svg { flex-shrink: 0; margin-top: 1px; }
`;

const SelectionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.8rem;
  color: ${p => p.theme?.colors?.textMuted || 'rgba(255,255,255,0.5)'};
  button {
    background: transparent;
    border: none;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    font-size: 0.8rem;
    cursor: pointer;
    padding: 0;
    &:hover { text-decoration: underline; }
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 340px;
  overflow-y: auto;
  margin-bottom: 1rem;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme?.colors?.accent || '#800000'}50; border-radius: 2px; }
`;

const ItemRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  background: ${p => p.checked
    ? `${p.theme?.colors?.accent || '#800000'}18`
    : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${p => p.checked
    ? `${p.theme?.colors?.accent || '#800000'}50`
    : 'rgba(255,255,255,0.08)'};
  transition: background 0.12s, border-color 0.12s;
  &:hover { background: ${p => `${p.theme?.colors?.accent || '#800000'}12`}; }
`;

const ItemThumb = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  flex-shrink: 0;
  overflow: hidden;
  background: rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
  svg { opacity: 0.3; }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${p => p.theme?.colors?.text || '#fff'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .meta {
    font-size: 0.76rem;
    color: ${p => p.theme?.colors?.textMuted || 'rgba(255,255,255,0.45)'};
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: ${p => p.theme?.colors?.accent || '#800000'};
`;

const ImportBtn = styled.button`
  width: 100%;
  background: ${p => p.theme?.colors?.accentGradient || p.theme?.colors?.accent || '#800000'};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.85rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:not(:disabled):hover { opacity: 0.88; }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.25);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const WebsiteScraper = ({ onClose, onProductsScraped, theme, isService }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('');
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const validateUrl = (u) => {
    try {
      const obj = new URL(u);
      return obj.protocol === 'http:' || obj.protocol === 'https:';
    } catch { return false; }
  };

  const scanWebsite = async () => {
    if (!url.trim()) { setStatus({ type: 'error', message: 'Please enter a URL' }); return; }
    if (!validateUrl(url)) { setStatus({ type: 'error', message: 'Enter a valid URL starting with http:// or https://' }); return; }

    setLoading(true);
    setStatus(null);
    setItems([]);
    setSelected(new Set());

    const label = isService ? 'services' : 'products';

    // ── Stage 1: import-shop (Shopify + JSON-LD, returns images) ───────────────
    setStage('Checking for product catalog…');
    try {
      const r = await fetch('/api/compress-media?action=import-shop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      if (r.ok) {
        const data = await r.json();
        if (data.items?.length > 0) {
          const valid = buildItems(data.items, true);
          if (valid.length > 0) {
            const withImages = valid.filter(i => i.images[0]).length;
            setItems(valid);
            setSelected(new Set(valid.map((_, i) => i)));
            setStatus({
              type: 'success',
              message: `Found ${valid.length} ${label}${withImages ? ` (${withImages} with images)` : ''} from ${data.shopMeta?.name || 'website'}.`
            });
            setLoading(false);
            return;
          }
        }
      }
    } catch (_) {}

    // ── Stage 2: scrape HTML (improved — also extracts images) ─────────────────
    setStage('Scanning page for products…');
    let pageText = '';
    let pageMeta = {};
    try {
      const r = await fetch('/api/compress-media?action=scrape', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      if (r.ok) {
        const data = await r.json();
        pageText = data.text || '';
        pageMeta = data.meta || {};

        if (data.products?.length > 0) {
          const valid = buildItems(data.products, true);
          if (valid.length > 0) {
            setItems(valid);
            setSelected(new Set(valid.map((_, i) => i)));
            setStatus({
              type: 'success',
              message: `Found ${valid.length} ${label} from ${pageMeta.siteName || 'website'}.`
            });
            setLoading(false);
            return;
          }
        }
      }
    } catch (_) {}

    if (!pageText) {
      setStatus({ type: 'error', message: 'Could not reach the website. It may be blocking automated requests.' });
      setLoading(false);
      return;
    }

    // ── Stage 3: Claude AI text analysis ──────────────────────────────────────
    setStage('Analyzing with AI…');
    try {
      const aiRes = await fetch('/api/compress-media?action=ai-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, text: pageText.slice(0, 15000), meta: pageMeta, isService })
      });

      if (aiRes.ok) {
        const aiData = await aiRes.json();
        if (aiData.items?.length > 0) {
          const valid = buildItems(aiData.items, false);
          setItems(valid);
          setSelected(new Set(valid.map((_, i) => i)));
          setStatus({ type: 'success', message: `AI found ${valid.length} ${label}. Review and import.` });
          setLoading(false);
          return;
        }
      }

      setStatus({ type: 'error', message: `No ${label} found on this page.` });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Analysis failed.' });
    } finally {
      setLoading(false);
    }
  };

  const buildItems = (raw, hasImageField) =>
    (raw || [])
      .filter(i => i.name?.trim())
      .slice(0, 30)
      .map(i => ({
        name: i.name.trim(),
        price: (i.price || '').toString().trim(),
        description: (i.description || '').trim(),
        category: i.category || 'Other',
        images: (hasImageField && i.image) ? [i.image, null, null] : [null, null, null],
        currentImageIndex: 0,
        ...(isService ? { slots: 1 } : { quantity: i.quantity || 1 }),
        address: '',
        coordinates: null
      }));

  const toggleAll = (select) =>
    setSelected(select ? new Set(items.map((_, i) => i)) : new Set());

  const toggleItem = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleImport = () => {
    const chosen = items.filter((_, i) => selected.has(i));
    if (chosen.length > 0) {
      onProductsScraped(chosen);
      onClose();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal theme={theme} onClick={e => e.stopPropagation()}>
        <Header theme={theme}>
          <h2><Globe size={22} /> Import from Website</h2>
          <CloseBtn theme={theme} onClick={onClose}><X size={20} /></CloseBtn>
        </Header>

        <UrlRow theme={theme}>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && scanWebsite()}
            placeholder="https://yourstore.com"
            disabled={loading}
          />
          <ScanBtn theme={theme} onClick={scanWebsite} disabled={loading || !url.trim()}>
            {loading ? <><Spinner /> {stage || 'Scanning…'}</> : <><Globe size={16} /> Scan</>}
          </ScanBtn>
        </UrlRow>

        {status && (
          <StatusMsg type={status.type}>
            {status.type === 'error'   && <AlertCircle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'info'    && <Loader size={16} />}
            <span>{status.message}</span>
          </StatusMsg>
        )}

        {items.length > 0 && (
          <>
            <SelectionBar theme={theme}>
              <span>{selected.size} / {items.length} selected</span>
              <span>
                <button onClick={() => toggleAll(true)}>Select all</button>
                {' · '}
                <button onClick={() => toggleAll(false)}>Deselect all</button>
              </span>
            </SelectionBar>

            <ItemList theme={theme}>
              {items.map((item, idx) => (
                <ItemRow key={idx} theme={theme} checked={selected.has(idx)} onClick={() => toggleItem(idx)}>
                  <Checkbox
                    theme={theme}
                    checked={selected.has(idx)}
                    onChange={() => toggleItem(idx)}
                    onClick={e => e.stopPropagation()}
                  />
                  <ItemThumb>
                    {item.images[0]
                      ? <img src={item.images[0]} alt={item.name} onError={e => { e.target.style.display='none'; }} />
                      : <ImageIcon size={18} />
                    }
                  </ItemThumb>
                  <ItemInfo theme={theme}>
                    <div className="name">{item.name}</div>
                    <div className="meta">
                      {item.price ? `$${parseFloat(item.price).toFixed(2)}` : 'No price'}
                      {item.description ? ` · ${item.description.slice(0, 60)}` : ''}
                    </div>
                  </ItemInfo>
                </ItemRow>
              ))}
            </ItemList>

            <ImportBtn theme={theme} onClick={handleImport} disabled={selected.size === 0}>
              <CheckCircle size={18} />
              Import {selected.size} {isService ? 'Service' : 'Product'}{selected.size !== 1 ? 's' : ''}
            </ImportBtn>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default WebsiteScraper;
